import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, MapPin, Rocket, Building, Globe, Sparkles } from 'lucide-react';

const RoadmapPage = () => {
  useEffect(() => {
    document.title = 'Roadmap & Milestones | Nexus LBE';
  }, []);

  const phases = [
    {
      phase: 'Phase 1',
      status: 'In Development',
      icon: Rocket,
      title: 'e.g. Berlin Pilot',
      timeline: '2025',
      description: 'Launch first pilot venue to validate format, operations, and guest experience.',
      milestones: [
        'Secure e.g. Berlin property partnership',
        'Complete modular build-out and tech installation',
        'Launch with initial IP title',
        'Validate 5-phase guest journey and operational playbooks',
        'Measure: NPS, repeat rate, revenue per guest, operational efficiency'
      ]
    },
    {
      phase: 'Phase 2',
      status: 'Planned',
      icon: Building,
      title: 'Flagship Rollout',
      timeline: '2026-2027',
      description: 'Owned and operated flagships in major European and GCC cities.',
      milestones: [
        'Launch flagship venues in e.g. London, e.g. Hamburg, and e.g. Riyadh',
        'Establish vendor partnerships and supply chain',
        'Build operational team and training programs',
        'Secure IP slate with major studios'
      ]
    },
    {
      phase: 'Phase 3',
      status: 'Planned',
      icon: Globe,
      title: 'Multi-City Expansion',
      timeline: '2028-2029',
      description: 'Scale to 10+ cities with franchise model introduction.',
      milestones: [
        'Further expand globally',
        'Introduce franchise model alongside owned & operated',
        'Launch membership network for cross-site access',
        'Develop proprietary IP titles',
        'Establish category leadership in Europe and GCC'
      ]
    },
    {
      phase: 'Phase 4',
      status: 'Vision',
      icon: Sparkles,
      title: 'Global Platform',
      timeline: '2030+',
      description: 'Become the defining platform for formatted immersive entertainment globally.',
      milestones: [
        'Operate 15+ sites across Europe, GCC, and new markets',
        'Mature franchise network with operational excellence',
        'Explore new formats and experience types',
        'Industry-leading NPS and guest satisfaction scores'
      ]
    }
  ];

  return (
    <div className="relative">
      {/* Hero */}
      <section className="relative overflow-hidden pt-16 pb-10 lg:pt-20 lg:pb-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl text-center">
          <Badge variant="outline" className="mb-4">Roadmap & Milestones</Badge>
          <h1 className="font-['Geist'] tracking-tight text-4xl sm:text-5xl lg:text-6xl mb-6">
            Building the future of LBE
          </h1>
          <p className="text-muted-foreground text-lg md:text-xl max-w-3xl mx-auto">
            Our phased approach to becoming the defining platform for formatted, immersive entertainment experiences.
          </p>
        </div>
      </section>

      {/* Coming Soon Banner */}
      <section className="py-6 bg-[#00F5FF]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
            <div className="flex items-center gap-3">
              <MapPin className="h-6 w-6" />
              <div>
                <p className="font-['Geist'] text-xl font-semibold">First NEXUS LBE Venue Coming Soon</p>
                <p className="text-sm text-muted-foreground">e.g. Berlin pilot launching 2025 - be part of the journey</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          <div className="space-y-8">
            {phases.map((phase, idx) => (
              <div key={idx} className="relative">
                {/* Connector line */}
                {idx < phases.length - 1 && (
                  <div className="absolute left-6 top-24 bottom-0 w-0.5 bg-border hidden md:block" />
                )}
                
                <Card>
                  <CardContent className="p-8 md:p-12">
                    <div className="flex flex-col md:flex-row gap-6">
                      {/* Icon & Status */}
                      <div className="flex-shrink-0">
                        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                          <phase.icon className="h-6 w-6 text-primary" />
                        </div>
                        <Badge variant={phase.status === 'In Development' ? 'default' : 'secondary'}>
                          {phase.status}
                        </Badge>
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 space-y-4">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-['Geist'] text-2xl">{phase.phase}: {phase.title}</h3>
                            <Badge variant="outline" className="text-xs">{phase.timeline}</Badge>
                          </div>
                          <p className="text-muted-foreground">{phase.description}</p>
                        </div>
                        
                        <div>
                          <h4 className="text-sm font-semibold mb-3">Key Milestones</h4>
                          <ul className="space-y-2">
                            {phase.milestones.map((milestone, mIdx) => (
                              <li key={mIdx} className="flex items-start gap-2 text-sm text-muted-foreground">
                                <div className="h-1.5 w-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                                <span>{milestone}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5-Year Vision */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
          <div className="text-center mb-8">
            <h2 className="font-['Geist'] text-3xl md:text-4xl mb-4">
              Our 5-year vision
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <Card>
              <CardContent className="p-6 text-center space-y-2">
                <div className="text-4xl font-['Geist'] font-bold text-primary">15+</div>
                <p className="text-sm font-semibold">Operating Sites</p>
                <p className="text-xs text-muted-foreground">Across Europe, GCC, and new markets</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 text-center space-y-2">
                <div className="text-4xl font-['Geist'] font-bold text-primary">Building</div>
                <p className="text-sm font-semibold">a digital Experience format LBE@Home</p>
                <p className="text-xs text-muted-foreground">Own original titles and formats</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6 text-center space-y-2">
                <div className="text-4xl font-['Geist'] font-bold text-primary">Category</div>
                <p className="text-sm font-semibold">Market Leadership</p>
                <p className="text-xs text-muted-foreground">Defining platform for formatted LBE</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 lg:py-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl text-center">
          <h2 className="font-['Geist'] text-3xl md:text-4xl mb-4">
            Join us on the journey
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto text-muted-foreground">
            Whether as an IP partner, property owner, or investor, there are multiple ways to be part of this vision.
          </p>
          <Link to="/contact">
            <Button size="lg" className="h-12 px-6 bg-[#FF6B9D] hover:bg-[#FF6B9D]/90 text-[#0F0B1F]">
              Explore Partnership Opportunities
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default RoadmapPage;
